import { defineStore } from "pinia";
import { PriceLayer } from "./entity";
import { PriceLayerList } from "./list.gen";
import type { PriceLayerStore, PriceLayerStoreState } from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const priceLayerList = new PriceLayerList();
export const usePriceLayerStore = defineStore("priceLayerStore", {
  state: (): PriceLayerStoreState => ({
    activeID: null,
    cache: {},
    list: [] as PriceLayer[],
    retrievalTimestamp: 0,
    updateKey: 0,
    storeUpdateTimeout: null,
  }),
  actions: {
    initializeStore(): void {
      this.convertList();
    },
    emitFormUpdateEvent(id: string): void {
      window.dispatchEvent(
        new CustomEvent("form-update-PriceLayer", { detail: { id } }),
      );
    },
    emitStoreUpdateEvent(): void {
      // Clear existing timeout
      if (this.storeUpdateTimeout !== null) {
        clearTimeout(this.storeUpdateTimeout);
      }

      // Set new timeout
      this.storeUpdateTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent("store.update.PriceLayer"));
        this.storeUpdateTimeout = null;
      }, 1500);
    },
    async activate(priceLayer: PriceLayer): Promise<void> {
      // Set active
      await priceLayer
        .activate(auth.firebaseUser?.uid)
        .then(() => {
          this.activeID = priceLayer.id;
        })
        .catch((error) => {
          console.error(error);
          this.activeID = null;
        });
    },
    add(priceLayer: PriceLayer): void {
      const id = priceLayer.id;
      if (typeof id === "string" && !this.contains(id)) {
        this.list.push(priceLayer);

        // Set active
        if (this.active === null) {
          this.activeID = priceLayer.id;
        }
      } else {
        this.reinsert(priceLayer);
      }
      this.updateKey += 1;
    },
    convertList(): void {
      this.list = (this.list as PriceLayer[]).map((x: any) => {
        const priceLayer = new PriceLayer({});
        priceLayer.set(x as JSONData);
        return priceLayer;
      });
    },
    async delete(priceLayer: PriceLayer): Promise<void> {
      await priceLayer
        .delete()
        .then(() => {
          this.remove(priceLayer);
        })
        .finally(() => {
          this.emitStoreUpdateEvent();
        });
    },
    async deleteById(id: string): Promise<void> {
      const priceLayer = (this.list as PriceLayer[]).find(
        (x: PriceLayer) => x.id === id,
      );
      if (priceLayer !== undefined) {
        await this.delete(priceLayer);
      } else {
        throw new Error("Could not find PriceLayer with id: " + id);
      }
    },
    contains(id: string): boolean {
      return (this.list as PriceLayer[]).some((x: PriceLayer) => x.id === id);
    },
    get(id: string): PriceLayer | null {
      return (
        (this.list as PriceLayer[]).find((x: PriceLayer) => x.id === id) || null
      );
    },
    reinsert(priceLayer: PriceLayer): void {
      const index = (this.list as PriceLayer[]).findIndex(
        (x: PriceLayer) => x.id === priceLayer.id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          priceLayer,
          ...this.list.slice(index + 1),
        ];
      } else {
        this.add(priceLayer);
      }
      this.updateKey += 1;
    },
    remove({ id }: PriceLayer): void {
      const index = (this.list as PriceLayer[]).findIndex(
        (x: PriceLayer) => x.id === id,
      );
      if (index !== -1) {
        this.list = [
          ...this.list.slice(0, index),
          ...this.list.slice(index + 1),
        ];
      }
      this.updateKey += 1;
    },
    async retrieve(id: string, force = false): Promise<PriceLayer> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        const priceLayer = this.get(id);
        if (priceLayer !== null) {
          return priceLayer;
        }
      }

      // Retrieve priceLayer
      const priceLayer = new PriceLayer({});

      // Get priceLayer
      await priceLayer.get([id]).then(async (entity) => {
        this.reinsert(entity);

        // Activate priceLayer
        await this.activate(entity);
      });

      return priceLayer;
    },
    async retrieveAll(force = false): Promise<{
      active: string | null;
      items: PriceLayer[];
    }> {
      // Check if a refresh is required
      if (!force && !this.refreshRequired) {
        return {
          active: this.activeID,
          items: this.list as PriceLayer[],
        };
      }

      // Clear list
      while (this.list.length > 0) {
        this.list.pop();
      }

      // Retrieve list
      await priceLayerList.getAll().then(async (list) => {
        list.forEach((priceLayer) => {
          this.reinsert(priceLayer);
        });

        // Activate priceLayer
        if (list.length > 0) {
          if (priceLayerList.active !== null) {
            this.activeID = priceLayerList.active;
          } else {
            await this.activate(list[0]);
          }
        }
      });

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      // Set user property
      if (this.activeID !== null) {
        analytics.setUserProperty("price_layer_id", this.activeID);
      }

      return {
        active: this.activeID,
        items: this.list as PriceLayer[],
      };
    },
    async retrievePaginated(
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PriceLayer[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as PriceLayer),
        };
      }

      // Retrieve list
      const items = await priceLayerList
        .getPaginated([], pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((priceLayer) => {
            this.reinsert(priceLayer);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PriceLayer) => x.id),
        pagination: newPagination,
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items,
      };
    },
    async query(
      queries: Query[],
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PriceLayer[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          pagination: cached.pagination.deepCopy(),
          items: cached.ids.map((id: string) => this.get(id) as PriceLayer),
        };
      }

      // Retrieve list
      const items = await priceLayerList
        .query(queries, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((priceLayer) => {
            this.reinsert(priceLayer);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PriceLayer) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        pagination: newPagination,
        items: items,
      };
    },
    async search(
      query: string,
      pagination: Pagination,
      force = false,
    ): Promise<{
      pagination: Pagination;
      items: PriceLayer[];
    }> {
      // Check pagination
      let newPagination = new Pagination(pagination).deepCopy();

      // Get cachekey
      const cacheKey =
        query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

      // Check if a refresh is required
      if (!force && this.cache[cacheKey]) {
        const cached = this.cache[cacheKey];
        return {
          items: cached.ids.map((id: string) => this.get(id) as PriceLayer),
          pagination: cached.pagination.deepCopy(),
        };
      }

      // Retrieve list
      const items = await priceLayerList
        .search(query, pagination)
        .then(async ({ items, pagination }) => {
          items.forEach((priceLayer) => {
            this.reinsert(priceLayer);
          });

          // Set new pagination
          newPagination = pagination.deepCopy();

          return items;
        });

      // Set cache
      this.cache[cacheKey] = {
        ids: items.map((x: PriceLayer) => x.id),
        pagination: newPagination.deepCopy(),
        retrievalTimestamp: Date.now(),
      };

      // Set retrieval timestamp
      this.retrievalTimestamp = Date.now();

      return {
        items: items,
        pagination: newPagination,
      };
    },
    async update(priceLayer: PriceLayer): Promise<string> {
      // Update priceLayer
      await priceLayer.update();

      // Reinsert priceLayer
      this.reinsert(priceLayer);

      // Activate priceLayer
      if (this.active === null) {
        await this.activate(priceLayer);
      }

      this.emitStoreUpdateEvent();

      return priceLayer.id;
    },
  },
  getters: {
    active(): PriceLayer | null {
      if (this.activeID === null) {
        return null;
      }

      const priceLayer = (this.list as PriceLayer[]).find(
        (x: PriceLayer) => x.id === this.activeID,
      );
      if (priceLayer === undefined) {
        return null;
      }

      return priceLayer;
    },
    refreshRequired(): boolean {
      return (
        this.retrievalTimestamp === 0 ||
        this.retrievalTimestamp < Date.now() - 30 * 60000 ||
        this.list.length === 0
      );
    },
  },
});

// Export a function to create a new instance of the store
export function createPriceLayerStore(): PriceLayerStore {
  const store = usePriceLayerStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as PriceLayerStore; // TODO: Fix this
}
